/**
 * Cliente de la API de Packs de Energía (US-2.4) — Admin Web.
 *
 * Endpoints del contrato (`openapi.yaml`, tag `packs-membresias`):
 * - GET    /api/v1/packs
 * - POST   /api/v1/packs
 * - GET    /api/v1/packs/{packId}
 * - PATCH  /api/v1/packs/{packId}
 * - DELETE /api/v1/packs/{packId} (baja lógica idempotente)
 *
 * Los tipos se derivan del contrato en `@goelectric/shared-types`; la red usa el
 * cliente HTTP centralizado (`apiRequest`) y su normalización de errores (`ApiError`).
 */
import type {
  CreatePackBody,
  EnergyPack,
  ListPacksQuery,
  PacksPage,
  UpdatePackBody,
} from '@goelectric/shared-types';
import { apiRequest } from '@/lib/http/apiClient';

/** Query params del listado (búsqueda, estado + offset/limit) tal como los define el contrato. */
export type PackListParams = ListPacksQuery;

/** Serializa los params definidos (omite `undefined`/`null`/vacío) a `?a=b&c=d`. */
function toQuery(params: PackListParams): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params) as [string, unknown][]) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

export const packApi = {
  list(params: PackListParams = {}): Promise<PacksPage> {
    return apiRequest<PacksPage>(`/api/v1/packs${toQuery(params)}`, {
      retryOnUnauthorized: true,
    });
  },

  get(packId: string): Promise<EnergyPack> {
    return apiRequest<EnergyPack>(`/api/v1/packs/${encodeURIComponent(packId)}`);
  },

  create(body: CreatePackBody): Promise<EnergyPack> {
    return apiRequest<EnergyPack>('/api/v1/packs', { method: 'POST', body });
  },

  update(packId: string, body: UpdatePackBody): Promise<EnergyPack> {
    return apiRequest<EnergyPack>(`/api/v1/packs/${encodeURIComponent(packId)}`, {
      method: 'PATCH',
      body,
    });
  },

  remove(packId: string): Promise<void> {
    return apiRequest<void>(`/api/v1/packs/${encodeURIComponent(packId)}`, {
      method: 'DELETE',
    });
  },
};
